import React, {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import { captchaEvent } from '../../../modules/metrics';

interface ProviderProps {
  children: ReactNode;
  recaptchaScriptLoaded?: boolean;
}

interface State {
  recaptchaScriptLoaded: boolean;
}

const MAX_ATTEMPTS = 2;
const RecaptchaContext = createContext<State | undefined>(undefined);

export const useRecaptchaContext = () => {
  const context = useContext(RecaptchaContext);

  if (context === undefined) {
    throw new Error('RecaptchaContext must be used within a RecaptchaProvider');
  }

  return context;
};

export const RecaptchaProvider = (props: ProviderProps) => {
  const [recaptchaScriptLoaded, setRecaptchaScriptLoaded] = useState(
    props.recaptchaScriptLoaded || false,
  );

  /**
   * * Retry loading script until MAX_ATTEMPTS reached
   * * On failure, log attempt number and how long call took
   */
  useEffect(() => {
    const loadScript = (domain: string, attemptNumber: number = 1) => {
      const script = document.createElement('script');
      script.src = `https://www.${domain}/recaptcha/enterprise.js?render=explict`;
      script.onerror = () => {
        captchaEvent('error_loading_recaptcha_library');
        if (attemptNumber < MAX_ATTEMPTS) {
          // If first attempt fails try with globally available domain to handle cases where country blocks 'google.com'
          // https://cloud.google.com/recaptcha-enterprise/docs/faq#can_i_use_globally
          loadScript('recaptcha.net', attemptNumber + 1);
        }
      };
      script.onload = () => {
        setRecaptchaScriptLoaded(true);
        if (attemptNumber > 1) {
          captchaEvent(
            'success_loading_recaptcha_library_after_initial_failure',
          );
        }
      };
      document.head.appendChild(script);
    };

    loadScript('google.com');
  }, [setRecaptchaScriptLoaded]);

  return (
    <RecaptchaContext.Provider value={{ recaptchaScriptLoaded }}>
      {props.children}
    </RecaptchaContext.Provider>
  );
};
